import { AlertTriangle, ArrowRight, Gauge } from "lucide-react";
import { Link } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/utils/common.utils";

/** Only the deal figures — the seat flags are deliberately not accepted here,
 *  so this component cannot regress into alarming on them. */
interface PlanUsage {
  at_deal_limit?: boolean;
  near_deal_limit?: boolean;
  deals_active?: number;
  deals_limit?: number;
  addons_available?: boolean;
}

interface PlanUsageBannerExtras {
  /** False on uncapped plans, where there is no limit an add-on could raise. */
  addonsAvailable?: boolean;
}

interface PlanUsageBannerProps extends PlanUsageBannerExtras {
  usage?: PlanUsage | null;
  organizationSlug?: string | null;
}

export default function PlanUsageBanner({ usage, organizationSlug, addonsAvailable = true }: PlanUsageBannerProps) {
  if (!usage || !organizationSlug) return null;

  // Active deals only, deliberately.
  //
  // This used to alarm on `at_deal_limit || at_seat_limit` while *displaying*
  // whichever resource was deal-related. Since a Solo plan includes exactly one
  // seat and the owner occupies it, `at_seat_limit` is true for every Solo org
  // from creation — so the banner shouted "Plan limit reached" over deal numbers
  // that were nowhere near a limit, on accounts with zero deals.
  //
  // Running out of deals blocks the core action and is worth interrupting for.
  // Using the single seat the plan sells is just the plan working; the invite
  // buttons already explain themselves when they're disabled.
  const atLimit = Boolean(usage.at_deal_limit);
  const nearLimit = !atLimit && Boolean(usage.near_deal_limit);
  if (!atLimit && !nearLimit) return null;

  const resource = "active deals";
  const used = usage.deals_active;
  const limit = usage.deals_limit;
  const percent = limit ? Math.min(100, Math.round(((used ?? 0) / limit) * 100)) : 0;
  const remaining = Math.max(0, Number(limit || 0) - Number(used || 0));
  const Icon = atLimit ? AlertTriangle : Gauge;

  return (
    <Alert
      variant={atLimit ? "destructive" : "default"}
      className={cn(
        "overflow-hidden rounded-2xl px-5 py-5 shadow-none [&>svg]:static [&>svg~*]:pl-0",
        atLimit ? "border-dash-rust/20 bg-dash-rust/10 text-dash-rust" : "border-dash-amber/20 bg-dash-amber/10 text-dash-ink"
      )}
      data-testid="plan-usage-banner"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3.5">
          <span className={cn("flex size-10 shrink-0 items-center justify-center rounded-xl", atLimit ? "bg-dash-rust/10" : "bg-dash-amber/10 text-dash-amber")} aria-hidden="true">
            <Icon className="size-5" />
          </span>
          <div className="min-w-0 flex-1">
            <AlertTitle className="text-base font-semibold text-current">
              {atLimit ? "Plan limit reached" : "You’re approaching your plan limit"}
            </AlertTitle>
            <AlertDescription className={cn("mt-1", atLimit ? "text-dash-rust/80" : "text-dash-muted")}>
              {used}/{limit} {resource} are in use.{" "}
              {atLimit
                ? addonsAvailable
                  ? "Add another deal, close one, or upgrade to open more."
                  : "Close or archive one, or upgrade to open more."
                : `${remaining} ${remaining === 1 ? "deal" : "deals"} remaining.`}
            </AlertDescription>
            <div className="mt-3 flex max-w-sm items-center gap-3">
              <Progress value={percent} className={cn("h-1.5 bg-white/80", atLimit ? "[&>div]:bg-dash-rust" : "[&>div]:bg-dash-amber")} />
              <span className="shrink-0 text-xs font-semibold text-current">{percent}%</span>
            </div>
          </div>
        </div>
        <Button variant={atLimit ? "dashboardPrimary" : "dashboardOutline"} size="lg" className="shrink-0" asChild>
          {/* Deep-links to the add-ons card when buying one more unit would
              resolve the block — "See plans" only ever named the tier jump. */}
          <Link to={`/o/${organizationSlug}/billing${atLimit && addonsAvailable ? "#addons" : ""}`} data-testid="plan-usage-banner-cta">
            {atLimit && addonsAvailable ? "Add more" : "See plans"} <ArrowRight data-icon="inline-end" />
          </Link>
        </Button>
      </div>
    </Alert>
  );
}
